/**
 * The Asset Advisor (`/advisor`, `advisor.view`): the caller's runs, or everyone's with `advisor.view.all` (who, when,
 * client, problem, top match, status). **New run** opens the run dialog (`advisor.run`); an Admin may delete every run
 * for one client. A run still working is polled every 3 s until it ends.
 */

import './advisor.css';

import { Link, useNavigate } from 'react-router-dom';
import { Compass, Plus, Search, SearchX, Trash2, User, Users } from 'lucide-react';

import type { AdvisorRunSummary } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { FilterSelect } from '@/ui/FilterSelect';
import { formatDateTime } from '@/ui/format';
import { PageHead } from '@/ui/PageHead';
import { Pagination } from '@/ui/Pagination';
import { ScoreValue } from '@/ui/ScoreValue';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { ViewToggle } from '@/ui/ViewToggle';

import { STATUS_OPTIONS, personName, statusLabel, statusTone } from './advisorUi';
import { DeleteClientDialog } from './DeleteClientDialog';
import { NewRunDialog } from './NewRunDialog';
import { useAdvisorList, type AdvisorListState } from './useAdvisorList';

const SCOPES = [
  { key: 'mine' as const, label: 'My runs', Icon: User },
  { key: 'all' as const, label: 'All runs', Icon: Users },
];

function Toolbar({ state }: { state: AdvisorListState }) {
  return (
    <div className="toolbar filter-panel">
      {state.canAll && (
        <ViewToggle label="Whose runs" options={SCOPES} value={state.params.scope} onChange={(scope) => state.update({ scope })} />
      )}
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          value={state.text}
          placeholder="Search client or problem"
          aria-label="Search client or problem"
          onChange={(e) => state.setText(e.target.value)}
        />
      </div>
      <FilterSelect
        label="Filter by status"
        allLabel="All statuses"
        value={state.params.status}
        options={STATUS_OPTIONS}
        onChange={(status) => state.update({ status: status as AdvisorListState['params']['status'] })}
      />
      {state.canDeleteClient && (
        <button type="button" className="btn btn-secondary" onClick={() => state.setDeleting(true)}>
          <Trash2 aria-hidden="true" /> Delete a client's runs
        </button>
      )}
      {state.canRun && (
        <button type="button" className="btn btn-primary toolbar-primary" onClick={() => state.setCreating(true)}>
          <Plus aria-hidden="true" /> New run
        </button>
      )}
    </div>
  );
}

function TopMatch({ run }: { run: AdvisorRunSummary }) {
  if (run.top_match === null) return <span className="hint">{run.status === 'succeeded' ? 'No strong match' : 'Not yet'}</span>;
  return (
    <>
      {run.top_match} <ScoreValue value={run.top_total} />
      {run.match_count > 1 && <div className="sub">and {run.match_count - 1} more</div>}
    </>
  );
}

function RunsTable({ runs, everyone }: { runs: AdvisorRunSummary[]; everyone: boolean }) {
  return (
    <div className="table-scroll">
      <table className="data zebra compact" aria-label="Advisor runs">
        <thead>
          <tr>
            <th scope="col">Client</th>
            <th scope="col">Problem</th>
            <th scope="col">Top match</th>
            <th scope="col">Status</th>
            {everyone && <th scope="col">Started by</th>}
            <th scope="col">Started</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id}>
              <th scope="row">
                <Link className="cell-link" to={`/advisor/${run.id}`}>{run.client}</Link>
                {run.industry && <div className="sub">{run.industry.name}</div>}
              </th>
              <td>{run.problem_excerpt}</td>
              <td><TopMatch run={run} /></td>
              <td><Badge tone={statusTone(run.status)}>{statusLabel(run.status)}</Badge></td>
              {everyone && <td>{personName(run.created_by)}</td>}
              <td className="hint">{formatDateTime(run.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ state }: { state: AdvisorListState }) {
  if (state.filtered) {
    return (
      <div className="empty">
        <div className="e-icon"><SearchX aria-hidden="true" /></div>
        <h3>No runs match</h3>
        <p>Try other words, or clear the search and the status.</p>
        <div className="empty-actions">
          <button type="button" className="btn btn-secondary" onClick={() => { state.setText(''); state.update({ q: '', status: null }); }}>
            Clear search and status
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="empty">
      <div className="e-icon"><Compass aria-hidden="true" /></div>
      <h3>No runs yet</h3>
      <p>Describe a client's problem and the Advisor finds the assets that address it, with the proof behind them.</p>
      {state.canRun && (
        <div className="empty-actions">
          <button type="button" className="btn btn-primary" onClick={() => state.setCreating(true)}>
            <Plus aria-hidden="true" /> New run
          </button>
        </div>
      )}
    </div>
  );
}

function Results({ state }: { state: AdvisorListState }) {
  const { list, params } = state;
  if (list.isPending) return <LoadingCard label="Loading runs" />;
  if (list.error) return <LoadFailure error={list.error} onRetry={() => void list.refetch()} />;
  if (list.data.runs.length === 0) return <Empty state={state} />;
  return (
    <>
      <RunsTable runs={list.data.runs} everyone={params.scope === 'all'} />
      <Pagination
        page={params.page}
        pageSize={params.page_size}
        total={list.data.total}
        noun={list.data.total === 1 ? 'run' : 'runs'}
        onPage={(page) => state.update({ page })}
        onPageSize={(page_size) => state.update({ page_size })}
      />
    </>
  );
}

export function AdvisorListPage() {
  const state = useAdvisorList();
  const navigate = useNavigate();
  return (
    <>
      <PageHead title="Asset Advisor" subtitle="Match a client's problem to the assets that address it" />
      <div className="card list-card">
        <Toolbar state={state} />
        <div aria-busy={state.list.isFetching}>
          <Results state={state} />
        </div>
      </div>
      {state.creating && (
        <NewRunDialog onClose={() => state.setCreating(false)} onStarted={(run) => navigate(`/advisor/${run.id}`)} />
      )}
      {state.deleting && <DeleteClientDialog onClose={() => state.setDeleting(false)} />}
    </>
  );
}
